import {BOARD_POSES,clamp,poseWeights,edgeWeights} from './board-motion.js';

// World +Z runs downhill; heading 0 points the board nose down +Z.
// In the rider glTF +X is the nose and +Z is the toe side. After the
// heading - PI/2 yaw, positive edge (local Rx) loads the world -X/toe edge.
// From the trailing camera at -Z, world -X is SCREEN RIGHT. Thus steer +1
// (D / screen right) loads the toe edge and produces negative world yaw.
// Tilt, board yaw, planar velocity, vertical motion and joint weights stay separate.
const STEP=1/120,GRAVITY=9.81;
const POSES=[...new Set([...BOARD_POSES,'carve_toe','carve_heel','nose_press','tail_press'])];
const smooth=(current,target,rate,dt)=>current+(target-current)*(1-Math.exp(-rate*dt));
const angleDelta=(a,b)=>Math.atan2(Math.sin(a-b),Math.cos(a-b));
const weightBlend=(a,b,t)=>Object.fromEntries(POSES.map(name=>[name,(a[name]||0)*(1-t)+(b[name]||0)*t]));
const singlePose=name=>Object.fromEntries(POSES.map(p=>[p,p===name?1:0]));
const ease=t=>{t=clamp(t);return t*t*(3-2*t);};
const copy=s=>({...s,weights:{...s.weights}});
const names={glide:'平板滑行',sideslip:'横板控速',stopped:'停留',carve:'刻滑',skidded_turn:'搓雪转弯',air:'腾空'};
const number=(value,fallback=0)=>Number.isFinite(value)?value:fallback;
// Keep the existing low-speed drag, then ease its coefficient down as the
// board gathers speed. Resistance rises more strongly above 81 km/h, so a
// sustained descent settles without a target-speed motor or a hard speed cap.
const airDrag=speed=>(.0030-.0010*ease((speed-14)/7))*speed+.020*Math.max(0,speed-22.5)**2;

export function sampleSlope(ground,x,z){
  const height=number(ground(x,z)),d=.18;
  const gx=(number(ground(x+d,z),height)-number(ground(x-d,z),height))/(d*2);
  const gz=(number(ground(x,z+d),height)-number(ground(x,z-d),height))/(d*2);
  return {height,gx,gz,ax:-GRAVITY*gx/(1+gx*gx+gz*gz),az:-GRAVITY*gz/(1+gx*gx+gz*gz)};
}

// These bounded forces and assists are arcade tuning, not a teaching formula.
// The interface accepts a height function rather than importing a particular map.
export function createAdventureMotion(initial={}){
  const state={};let previous,accumulator=0,jumpWasHeld=false,brakeSide=1,launchWeights=poseWeights(),landingWeights=poseWeights(),pressWasWanted=false,aligning=false,alignmentHalfTurns=0,spinActive=false;
  function reset(pose={}){
    const x=number(pose.x),z=number(pose.z),heading=number(pose.heading),speed=Math.max(0,number(pose.speed,2.4));
    Object.assign(state,{x,z,heading,vx:number(pose.vx,Math.sin(heading)*speed),vz:number(pose.vz,Math.cos(heading)*speed),speed,
      edge:0,lean:0,pitch:0,height:0,lift:0,vy:0,worldY:0,groundHeight:0,slip:0,sideSpeed:0,pressure:1,edgeLoad:0,yawRate:0,curvature:0,turnRadius:10000,carvePose:0,handDragRequest:0,
      press:0,pressPivot:0,pressLift:0,pressRelease:0,pressBlocked:false,groundSpin:0,spinDegrees:0,completedSpin:0,stance:'regular',
      contact:true,spray:0,trailWidth:0,trailOpacity:0,technique:'glide',stage:names.glide,phase:'ground',
      charge:0,takeoffCharge:0,phaseAge:0,airAge:0,steering:0,braking:0,carving:0,weights:poseWeights(),time:0,distance:0});
    accumulator=0;jumpWasHeld=false;brakeSide=1;launchWeights=poseWeights();landingWeights=poseWeights();pressWasWanted=false;aligning=false;alignmentHalfTurns=0;spinActive=false;previous=copy(state);return state;
  }
  reset(initial);
  function clearInput(){jumpWasHeld=false;}
  function step(raw,ground){
    const h=STEP,s=state,input={steer:clamp(number(raw.steer),-1,1),brake:clamp(Number(raw.brake)||0),carve:clamp(Number(raw.carve)||0),jump:Boolean(raw.jump),press:clamp(number(raw.press),-1,1)};
    const slope=sampleSlope(ground,s.x,s.z),oldY=slope.height;
    const released=jumpWasHeld&&!input.jump;jumpWasHeld=input.jump;
    s.steering=smooth(s.steering,input.steer,9,h);
    s.braking=smooth(s.braking,input.brake,8,h);
    s.carving=smooth(s.carving,input.brake?0:input.carve,5,h);
    const grounded=s.phase!=='air';
    const speed=Math.hypot(s.vx,s.vz);
    const travelBefore=Math.atan2(s.vx,s.vz),forwardSpeed=s.vx*Math.sin(s.heading)+s.vz*Math.cos(s.heading);
    const slip=angleDelta(Math.atan2(s.vx,s.vz),s.heading),axisSlip=Math.min(Math.abs(slip),Math.PI-Math.abs(slip));
    const pressWanted=Math.abs(input.press)>.1&&grounded&&s.phase==='ground'&&!input.brake&&!input.jump&&s.charge<.01&&speed>.65&&speed<10.5;
    s.pressBlocked=Math.abs(input.press)>.1&&speed>=10.5;
    if(pressWasWanted&&!pressWanted&&Math.abs(s.press)>.08){
      // Finish near either forward OR switch. The heading is not wrapped, so
      // repeated 180/360 ground rotations retain their accumulated angle.
      alignmentHalfTurns=Math.round((s.heading-travelBefore)/Math.PI);aligning=!input.brake&&grounded;s.pressRelease=1;
    }
    if(pressWanted&&!pressWasWanted){aligning=false;if(!spinActive){s.groundSpin=0;s.completedSpin=0;spinActive=true;}}
    pressWasWanted=pressWanted;if(input.brake||!grounded)aligning=false;
    const pressTarget=pressWanted?input.press*(1-ease((Math.abs(s.edge)-.055)/.17)):0;
    s.press+=clamp(smooth(s.press,pressTarget,7,h)-s.press,-2.8*h,2.8*h);if(Math.abs(s.press)<.0001)s.press=0;
    const pressBlend=Math.abs(s.press);s.pressPivot=s.press*.45;s.pressLift=pressBlend*.10;
    s.pressRelease=smooth(s.pressRelease,aligning?1:0,7,h);
    const freePivot=Math.max(pressBlend,s.pressRelease);
    // Low speed cannot support a carving lean. Grip builds progressively only
    // when the board is already close enough to its travel line to hold an edge.
    const speedSupport=ease((Math.abs(forwardSpeed)-4.2)/3.8),lineSupport=1-ease((axisSlip-.16)/.42);
    const edgeRequest=grounded&&!input.brake&&!pressWanted&&freePivot<.05&&s.phase!=='pop'&&s.charge<.01?s.carving*speedSupport*lineSupport:0;
    s.edgeLoad=smooth(s.edgeLoad,edgeRequest,edgeRequest>s.edgeLoad?4:8,h);
    const downhill=Math.hypot(slope.ax,slope.az)>.015?Math.atan2(slope.ax,slope.az):0;
    if(input.brake&&s.braking<.08){
      const plus=downhill+Math.PI/2,minus=downhill-Math.PI/2;
      brakeSide=Math.abs(angleDelta(plus,s.heading))<=Math.abs(angleDelta(minus,s.heading))?1:-1;
      if(Math.abs(s.steering)>.2&&Math.abs(angleDelta(downhill,s.heading))<.4)brakeSide=-Math.sign(s.steering);
    }
    const toeX=-Math.cos(s.heading),toeZ=Math.sin(s.heading);
    const downhillAcross=slope.ax*toeX+slope.az*toeZ;
    const strength=Math.abs(s.steering),direction=Math.sign(s.steering)*Math.sign(forwardSpeed||1);
    // Release lateral support only after the actual board has flattened. A
    // held carve button alone cannot keep a flat base locked across the slope.
    // Gravity can then redirect travel; no route or desired speed is consulted.
    const flatBaseRelease=s.phase==='ground'&&!input.brake&&!input.jump&&s.charge<.001&&freePivot<.01?
      (1-ease(Math.abs(input.steer)/.08))*(1-ease((strength-.04)/.20))*(1-ease((Math.abs(s.edge)-.025)/.18)):0;
    // At speed, a full direction request must not yaw the board faster than
    // the snow can redirect its travel. That used to create a perpetual skid
    // whose energy loss exceeded gravity even through ordinary linked turns.
    // Keep low-speed agility and widen the high-speed arc continuously.
    const skidRate=.20+.70*speed/(speed+5);
    const skidYaw=-s.steering*skidRate/Math.sqrt(1+(speed*skidRate/9)**2);
    // A radius request becomes a balanced centripetal lean, not a button-sized
    // pose. The radius widens at high speed when the supported lean is reached.
    const minimumRadius=Math.max(18,speed*speed/12);
    // Zero steering means zero requested curvature, even while the smoothed
    // input is approaching zero from a previous turn. A radius floor used to
    // leave a small self-sustaining turn after releasing the direction control.
    const requestedCurvature=strength**.82/minimumRadius;
    const carveLean=Math.atan(Math.min(speed*speed*requestedCurvature,12)/GRAVITY);
    const angulation=.075*ease(strength/.3)*speedSupport;
    const skidLean=Math.atan(Math.abs(speed*skidYaw)*.48/GRAVITY);
    // Balance follows the measured travel arc, including its direction during
    // an edge change. A new key request alone cannot flip the body lean.
    let targetLean=-Math.sign(forwardSpeed||1)*Math.atan(clamp(speed*speed*s.curvature,-12,12)/GRAVITY);
    let targetEdge=direction*((skidLean+.055*strength)*(1-s.edgeLoad)+(carveLean+angulation)*s.edgeLoad);
    if(s.braking>.025){
      // A sideslip loads the UPHILL edge, whichever side is facing uphill.
      const uphillEdge=-Math.sign(downhillAcross||brakeSide)*(.37+input.brake*.08);
      targetEdge=targetEdge*(1-s.braking)+uphillEdge*s.braking;
      targetLean=targetLean*(1-s.braking)+uphillEdge*.55*s.braking;
    }
    if(pressWanted||freePivot>.05){
      // The end of the board carries a press, not a toe/heel edge. A tiny
      // uphill safety tilt follows the rotating board through a ground spin.
      const microEdge=-Math.sign(downhillAcross||0)*.018;
      targetEdge=targetEdge*(1-freePivot)+(pressWanted?microEdge:0)*freePivot;
      if(pressWanted&&pressBlend<.05)targetEdge=0;
      targetLean*=1-freePivot;
    }
    if(!grounded||s.phase==='pop'||s.charge>.01){targetEdge=0;targetLean=0;}
    const edgeNext=smooth(s.edge,clamp(targetEdge,-.96,.96),grounded?6.8:12,h);
    s.edge+=clamp(edgeNext-s.edge,-1.5*h,1.5*h);
    s.lean+=clamp(smooth(s.lean,targetLean,6.8,h)-s.lean,-1.5*h,1.5*h);
    // Pitch is only the small end-to-end press angle. A 1.585 m lever from
    // x = +/- .45 to the opposite end gains about .10 m at .064 radians.
    // Authored knee/hip poses move the mass beyond the supporting foot;
    // pitch cannot replace that pose or the renderer's actual contact check.
    s.pitch=s.press?-s.press*.064:0;
    if(grounded){
      const loadedLean=Math.max(0,Math.abs(s.edge)-angulation);
      const carveYaw=-Math.sign(s.edge)*Math.sign(forwardSpeed||1)*Math.min(12,GRAVITY*Math.tan(loadedLean))/Math.max(4.2,Math.abs(forwardSpeed));
      let yaw=skidYaw*(1-s.edgeLoad)+carveYaw*s.edgeLoad;
      if(s.braking>.025){
        // Direction remains live during braking: change the board's angle to
        // the fall line, then release brake to resume the chosen line.
        const target=downhill+brakeSide*Math.PI/2+s.steering*.3;
        yaw=yaw*(1-s.braking)+clamp(angleDelta(target,s.heading)*2.2,-1.45,1.45)*s.braking;
      }else if(Math.abs(s.steering)<.08&&speed>.35){
        // Small passive steering assist settles the board onto its existing
        // travel line after a skid; it never rotates the velocity by fiat.
        const travel=Math.atan2(s.vx,s.vz),difference=angleDelta(travel,s.heading);
        const closest=Math.abs(difference)>Math.PI/2?angleDelta(travel+Math.PI,s.heading):difference;
        const passiveLimit=.32+.28*flatBaseRelease;
        yaw+=clamp(closest*(.65+1.15*flatBaseRelease),-passiveLimit,passiveLimit)*(1-s.edgeLoad*(1-flatBaseRelease));
      }
      if(freePivot>.01&&!input.brake){
        let pivotYaw=-s.steering*2.1*pressBlend;
        if(aligning){
          const delta=angleDelta(travelBefore+alignmentHalfTurns*Math.PI,s.heading);
          pivotYaw+=clamp(delta*3.5,-2.1,2.1)*(1-pressBlend);
          if(Math.abs(delta)<.035&&pressBlend<.05)aligning=false;
        }
        yaw=yaw*(1-freePivot)+pivotYaw*freePivot;
      }
      s.yawRate=smooth(s.yawRate,yaw,8,h);s.heading+=s.yawRate*h;
      s.vx+=slope.ax*h;s.vz+=slope.az*h;
      if(freePivot>.01&&!input.brake&&speed>.3){
        // Small pressure steering still bends the travel line during a spin;
        // most angular motion belongs to the board, not the downhill velocity.
        const force=-s.steering*.72*freePivot;s.vx+=Math.cos(travelBefore)*force*h;s.vz-=Math.sin(travelBefore)*force*h;
      }
      const fx=Math.sin(s.heading),fz=Math.cos(s.heading),tx=-fz,tz=fx;
      let along=s.vx*fx+s.vz*fz,across=s.vx*tx+s.vz*tz;
      const grip=(1.35+.65*ease((speed-4)/8)+Math.abs(s.edge)*1.3+s.edgeLoad*30)*(1-s.braking*.68)*(1-freePivot)+freePivot*.065;
      across*=Math.exp(-(grip*(1-flatBaseRelease)+.28*flatBaseRelease)*h);
      // A thin loose surface covers a supporting snow base. The edge can grip,
      // while a skid dissipates its transverse velocity. Air resists travel in
      // every board orientation, including a flat-base ground spin.
      along*=Math.exp(-.010*h);
      const air=Math.exp(-airDrag(speed)*h);
      s.vx=(along*fx+across*tx)*air;s.vz=(along*fz+across*tz)*air;
      // Coulomb-like braking includes a static hold. Unlike velocity damping,
      // it can counter gravity and hold completely still on a normal slope.
      const afterSpeed=Math.hypot(s.vx,s.vz),brakeForce=s.braking*7.4;
      if(afterSpeed>0){const factor=Math.max(0,1-brakeForce*h/afterSpeed);s.vx*=factor;s.vz*=factor;}
      if(input.brake&&s.braking>.85&&Math.hypot(s.vx,s.vz)<.045){s.vx=0;s.vz=0;}
    }else{
      // Air steering changes board orientation, not the ballistic travel line.
      s.yawRate=smooth(s.yawRate,-s.steering*.38,8,h);s.heading+=s.yawRate*h;const air=Math.exp(-airDrag(speed)*h);s.vx*=air;s.vz*=air;
    }
    const oldX=s.x,oldZ=s.z;s.x+=s.vx*h;s.z+=s.vz*h;
    const newY=number(ground(s.x,s.z),oldY),surfaceVy=(newY-oldY)/h;
    s.distance+=Math.hypot(s.x-oldX,s.z-oldZ);s.time+=h;s.groundHeight=newY;
    if(grounded){s.worldY=newY;s.height=0;s.vy=surfaceVy;}
    const deepTarget=s.contact?s.edgeLoad*ease((speed-8)/6)*ease((Math.abs(s.edge)-.43)/.4):0;
    s.carvePose=Math.min(smooth(s.carvePose,deepTarget,8,h),ease((Math.abs(s.edge)-.22)/.45));
    s.handDragRequest=s.carvePose*ease((Math.abs(s.edge)-.72)/.2);
    const edgePose=weightBlend(edgeWeights(s.edge),singlePose(s.edge>=0?'carve_toe':'carve_heel'),s.carvePose);
    const baseWeights=weightBlend(edgePose,singlePose(s.press>=0?'nose_press':'tail_press'),pressBlend);
    if(s.phase==='ground'){
      s.weights=baseWeights;
      if(input.jump&&!input.brake&&pressBlend<.12)s.charge=clamp(s.charge+h/.7);
      if(s.charge>0)s.weights=weightBlend(baseWeights,poseWeights('charge'),ease(s.charge));
      if(released&&s.charge>.025&&!input.brake){
        s.phase='pop';s.phaseAge=0;s.takeoffCharge=s.charge;launchWeights={...s.weights};
      }else if(!input.jump||input.brake){s.charge=Math.max(0,s.charge-h*4);if(s.charge>0)s.weights=weightBlend(baseWeights,poseWeights('charge'),ease(s.charge));}
    }
    if(s.phase==='pop'){
      s.phaseAge+=h;s.weights=weightBlend(launchWeights,poseWeights('extend'),ease(s.phaseAge/.18));
      if(s.phaseAge>=.18){s.phase='air';s.airAge=0;s.phaseAge=0;s.vy=surfaceVy+3.65+s.takeoffCharge*1.65;s.charge=0;}
    }
    if(s.phase==='air'){
      s.airAge+=h;s.phaseAge+=h;s.worldY+=s.vy*h-GRAVITY*h*h/2;s.vy-=GRAVITY*h;s.height=Math.max(0,s.worldY-newY);
      s.weights=poseWeights('extend','air',s.airAge/.18);
      const relativeVy=s.vy-surfaceVy;
      const remaining=(relativeVy+Math.sqrt(relativeVy*relativeVy+2*GRAVITY*s.height))/GRAVITY;
      if(remaining<.2){
        s.weights=poseWeights('air','idle',1-remaining/.2);
        if(speed>.5){const travel=Math.atan2(s.vx,s.vz),difference=angleDelta(travel,s.heading),nearest=Math.abs(difference)>Math.PI/2?angleDelta(travel+Math.PI,s.heading):difference;s.heading+=clamp(nearest,-h*3,h*3)*(1-remaining/.2);}
      }
      if(s.worldY<=newY&&s.airAge>h){s.phase='land';s.phaseAge=0;s.worldY=newY;s.height=0;s.vy=surfaceVy;landingWeights={...s.weights};}
    }
    if(s.phase==='land'){
      s.phaseAge+=h;
      s.weights=s.phaseAge<.18?weightBlend(landingWeights,poseWeights('land'),ease(s.phaseAge/.18)):weightBlend(poseWeights('land'),baseWeights,ease((s.phaseAge-.18)/.62));
      if(s.phaseAge>=.8){s.phase='ground';s.phaseAge=0;s.weights=baseWeights;}
    }
    s.lift=s.height;s.contact=s.phase!=='air';s.speed=Math.hypot(s.vx,s.vz);
    s.slip=s.speed>.05?angleDelta(Math.atan2(s.vx,s.vz),s.heading):0;
    s.sideSpeed=s.vx*-Math.cos(s.heading)+s.vz*Math.sin(s.heading);
    const slipAxis=Math.min(Math.abs(s.slip),Math.PI-Math.abs(s.slip));
    const travelAfter=Math.atan2(s.vx,s.vz),travelDistance=Math.hypot(s.x-oldX,s.z-oldZ);
    if(spinActive&&(freePivot>.05||aligning))s.groundSpin+=s.yawRate*h-angleDelta(travelAfter,travelBefore);
    else if(spinActive&&!pressWanted&&freePivot<=.05){spinActive=false;const halfTurns=Math.round(s.groundSpin/Math.PI);s.completedSpin=Math.abs(s.groundSpin-halfTurns*Math.PI)<.22?Math.abs(halfTurns)*180:0;}
    s.spinDegrees=s.groundSpin*180/Math.PI;s.stance=Math.cos(s.slip)<0?'switch':'regular';
    const actualCurvature=s.contact&&s.speed>.4&&travelDistance>1e-6?angleDelta(travelAfter,travelBefore)/travelDistance:0;
    s.curvature=smooth(s.curvature,actualCurvature,7,h);s.turnRadius=Math.abs(s.curvature)>.0001?Math.min(10000,1/Math.abs(s.curvature)):10000;
    s.pressure=s.contact?clamp(Math.sqrt(1+(s.speed*s.speed*s.curvature/GRAVITY)**2),1,2.2)+(s.phase==='land'?.8*Math.sin(Math.PI*clamp(s.phaseAge/.8)):0):0;
    s.spray=s.contact&&s.speed>.2?clamp(Math.abs(s.sideSpeed)*.23+s.braking*s.speed*.04)*s.pressure:0;
    s.trailWidth=s.contact&&s.speed>.15?.027+clamp(Math.abs(s.sideSpeed)/2.8)*(.22+1.4*Math.abs(Math.sin(s.slip)))+s.braking*.12:0;
    if(s.contact&&s.speed>.2&&pressBlend>0){s.trailWidth=s.trailWidth*(1-pressBlend)+(.08+.16*Math.abs(Math.sin(s.slip)))*pressBlend;s.spray=s.spray*(1-pressBlend)+clamp(.04+Math.abs(s.yawRate)*.035+s.speed*.006)*pressBlend;}
    s.trailOpacity=s.contact&&s.speed>.15?clamp(.16+Math.abs(s.edge)*.8+Math.abs(s.sideSpeed)*.08,.16,.65):0;
    s.technique=!s.contact?'air':s.speed<.15?'stopped':s.braking>.2&&slipAxis>.25?'sideslip':Math.abs(s.edge)>.065&&s.speed>2?(s.edgeLoad>.65&&s.speed>6.2&&slipAxis<.085&&Math.abs(s.curvature)>.002?'carve':'skidded_turn'):slipAxis>.25?'sideslip':'glide';
    if(s.contact&&pressBlend>.15)s.technique=Math.abs(s.yawRate)>.35?'ground_spin':s.press>0?'nose_press':'tail_press';
    s.stage=s.phase==='pop'?'伸展离地':s.phase==='air'?'腾空收腿':s.phase==='land'?'落地吸收':s.charge>.05?'蓄力 · 松开起跳':s.pressBlocked?'先控速，再做平花':pressBlend>.15?(s.press>0?'板头 Press':'板尾 Press')+(Math.abs(s.yawRate)>.35?' · 地面转板':''):aligning?'平花收板 · 顺势衔接':names[s.technique];
  }
  function update(dt,input={},ground=()=>0){
    // Long tab stalls are paused by the caller; do not fast-forward the rider.
    if(state.time===0){const height=number(ground(state.x,state.z));state.worldY=height;state.groundHeight=height;previous.worldY=height;previous.groundHeight=height;}
    accumulator+=clamp(number(dt),0,.1);
    while(accumulator+1e-12>=STEP){previous=copy(state);step(input,ground);accumulator-=STEP;}
    accumulator=Math.max(0,accumulator);const alpha=accumulator/STEP,result=copy(state);
    for(const key of ['x','z','heading','vx','vz','speed','edge','lean','edgeLoad','yawRate','curvature','turnRadius','carvePose','handDragRequest','press','pressPivot','pressLift','pressRelease','groundSpin','spinDegrees','pitch','height','lift','worldY','groundHeight','sideSpeed','pressure','spray','trailWidth','trailOpacity'])result[key]=previous[key]+(state[key]-previous[key])*alpha;
    result.slip=previous.slip+angleDelta(state.slip,previous.slip)*alpha;
    result.contact=state.contact&&previous.contact;
    result.weights=weightBlend(previous.weights,state.weights,alpha);return result;
  }
  return {state,update,reset,clearInput};
}
